import * as React from "react";
import ChevronLeftIcon from "@src/lib/icons/12px/chevron-left";
import ChevronRightIcon from "@src/lib/icons/12px/chevron-right";
import { DayPicker } from "react-day-picker";

import { cn } from "@src/lib/utils/cn";
import { buttonVariants } from "@src/lib/ui/Button";

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: CalendarProps): React.ReactElement {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      // `relative` anchors the absolutely-positioned nav below. Without it the
      // nav escapes to whatever positioned ancestor it lands in (in a Dialog,
      // DialogContent itself) and the arrows drift away from the calendar.
      className={cn("relative p-3", className)}
      classNames={{
        months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
        month: "space-y-4 text-[14px] font-medium",
        // px-8 keeps the month label clear of the two 28px nav arrows.
        month_caption: "relative flex h-7 items-center justify-center px-8",
        caption_label: "text-sm font-medium",
        // react-day-picker v9 renders `nav` as a sibling of `month_caption`,
        // not a child of it, so the arrows can no longer be positioned against
        // the caption. Lay the nav across the caption row instead. `z-10` keeps
        // the arrows above `month_caption`, which is `relative` and painted
        // later, and would otherwise swallow every click on them.
        nav: "absolute left-3 right-3 top-3 z-10 flex h-7 items-center justify-between",
        button_previous: cn(
          buttonVariants({ variant: "outline" }),
          "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100",
        ),
        button_next: cn(
          buttonVariants({ variant: "outline" }),
          "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100",
        ),
        month_grid: "w-full border-collapse space-y-1",
        weekdays: "flex",
        weekday:
          "text-muted-foreground rounded-md w-[36px] font-normal text-[0.8rem]",
        week: "flex w-full mt-2",
        day: cn(
          "relative p-0 text-center text-[14px] focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-blue-2 [&:has([aria-selected].outside)]:bg-blue-1 [&:has([aria-selected].range_end)]:rounded-r-md",
          props.mode === "range"
            ? "[&:has(>.range_end)]:rounded-r-md [&:has(>.range_start)]:rounded-l-md first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md"
            : "[&:has([aria-selected])]:rounded-md",
        ),
        day_button: cn(
          buttonVariants({ variant: "ghost" }),
          // buttonVariants ships `rounded-full`, but every shape in this
          // calendar is rounded-md/lg — a circular hover/focus fill inside a
          // rounded-square cell read as a misshapen blob.
          "h-[36px] w-[36px] p-0 font-normal !rounded-lg aria-selected:opacity-100",
        ),
        range_start: "range-start",
        range_end: "range-end",
        // Deliberately NOT !important. In range mode the middle of the range is
        // painted by the lighter `day`/`range_middle` rules, which a forced
        // bg-primary here overrides — turning the range band into one solid
        // saturated block instead of a light band with solid end caps.
        selected:
          "bg-primary text-background hover:bg-primary-hover hover:text-background focus:bg-primary focus:text-background !rounded-lg",
        today: "bg-blue-2 text-blue-13 !rounded-lg",
        outside:
          "outside text-muted-foreground opacity-50 aria-selected:bg-blue-1 aria-selected:text-gray-13 aria-selected:opacity-30",
        disabled: "text-gray-13 opacity-50",
        range_middle: "aria-selected:bg-blue-2 aria-selected:text-blue-13",
        hidden: "invisible",
        ...classNames,
      }}
      components={{
        Chevron: ({ orientation }) =>
          orientation === "left" ? (
            <ChevronLeftIcon className="size-4" />
          ) : (
            <ChevronRightIcon className="size-4" />
          ),
      }}
      {...props}
    />
  );
}
Calendar.displayName = "Calendar";

export { Calendar };
